import { Injectable, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FuncaoService, Login, GlobalService, ProgressoService, Relatorio } from 'eddydata-lib';
import { DemonstrativoAuxiliarDeGastosService } from './demonstrativo-auxiliar-de-gastos.service';
import { Alignment, Workbook } from 'exceljs';

export type FormatoDag = 'pdf' | 'xlsx' | 'csv';

interface DAG {
    despesa_codigo: string;
    despesa_nome: string;
    sub_codigo: string;
    sub_nome: string;
    recurso_codigo: string;
    recurso_nome: string;
    despesa_pai_nome: string;
    despesa_pai_codigo: string;
    liquidado: string;
    orcado: string;
    orcado_atual: string;
}

interface DAGCompleta extends DAG {
    uo_codigo: string;
    uo_nome: string;
}

@Injectable({
    providedIn: 'root'
})
export class DemonstrativoAuxiliarDeGastosRPT implements OnDestroy {
    protected funcaoService: FuncaoService;
    private login: Login = new Login();
    protected unsubscribe: Subject<void> = new Subject();

    private quadrimestres = {
        '4': '1º Quadrimestre',
        '8': '2º Quadrimestre',
        '12': '3º Quadrimestre'
    }

    constructor(
        protected demonstrativoAuxiliarDeGastosService: DemonstrativoAuxiliarDeGastosService,
        protected globalService: GlobalService,
        protected progressoService: ProgressoService) {
        this.funcaoService = new FuncaoService();
        this.login = GlobalService.obterSessaoLogin();
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public montarRelatorio(exercicio: number, ppa: number, mes: number, quadrimestre: 4 | 8 | 12, acumulado: boolean, dtInicio?: string, dtFinal?: string, formato: FormatoDag = 'xlsx', ano?: number) {
        this.demonstrativoAuxiliarDeGastosService.obterDemonstrativoAuxiliarDeGastos(exercicio, ppa, mes, quadrimestre, acumulado, dtInicio, dtFinal)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(res => {
                this.progressoService.show(res, (dados: DAGCompleta[]) => {
                    const algCenter: Partial<Alignment> = { vertical: 'middle', horizontal: 'center' };
                    const algRight: Partial<Alignment> = { vertical: 'middle', horizontal: 'right' };
                    const algLeft: Partial<Alignment> = { vertical: 'middle', horizontal: 'left' };

                    // linhas equivalentes usadas apenas quando o formato solicitado é 'pdf'
                    const linhasPdf: any[][] = [];

                    const wb = new Workbook();
                    wb.creator = this.login.usuario.nome;
                    wb.lastModifiedBy = this.login.usuario.nome;
                    wb.created = new Date();
                    wb.modified = new Date();
                    wb.calcProperties.fullCalcOnLoad = true;

                    const ws = wb.addWorksheet('DAGs');

                    const titulo = ws.getCell('B1');
                    titulo.value = 'Demonstrações Auxiliares de Gastos - DAGs';
                    titulo.alignment = algCenter;

                    const subtitulo = ws.getCell('B2');
                    const anoExercicio = ano ?? exercicio;
                    let subtituloTexto: string;
                    if (dtInicio && dtFinal) {
                        subtituloTexto = `${this.funcaoService.converteDataBR(dtInicio)} à ${this.funcaoService.converteDataBR(dtFinal)}`;
                    } else if (acumulado) {
                        subtituloTexto = `${this.quadrimestres[`${quadrimestre}`]}/${anoExercicio}`;
                    } else {
                        let mesExtenso = this.globalService.obterMes(+mes)
                        subtituloTexto = `${mesExtenso}/${anoExercicio}`
                    }
                    subtitulo.value = subtituloTexto;

                    subtitulo.alignment = algCenter;

                    if (formato !== 'csv') {
                        ws.mergeCells('B1:F1');
                        ws.mergeCells('B2:F2');
                    }

                    const grupoUnidades = this.funcaoService.agrupar(dados, ['uo_codigo', 'uo_nome']);

                    const totalColunasPdf = 1 + grupoUnidades.length + 6;

                    const fontSizePdf = grupoUnidades.length > 20 ? 4 : grupoUnidades.length > 14 ? 4 : grupoUnidades.length > 8 ? 6 : 7;
                    const fontSizeTotalPdf = fontSizePdf + 1;
                    const larguraUtilPdf = 26 * 28.3465;
                    const larguraClassificacaoPdf = 25;
                    const larguraColunaTotalPdf = fontSizePdf * 7;
                    const larguraColunaPercentualPdf = fontSizePdf * 5;
                    const larguraColunaUnidadePdf = Math.max(
                        fontSizePdf * 5,
                        (larguraUtilPdf - larguraClassificacaoPdf - larguraColunaTotalPdf * 4 - larguraColunaPercentualPdf * 2) / Math.max(1, grupoUnidades.length)
                    );

                    // Montando Cabeçalho
                    const textoUnidades: string[] = grupoUnidades.map(group => `${group.grupo['uo_codigo']} ${group.grupo['uo_nome']}`)

                    const linhaCabecalho = ws.getRow(4);

                    linhaCabecalho.getCell(1).value = 'Classificação';
                    linhaCabecalho.getCell(1).alignment = algLeft;

                    const fimColunas = textoUnidades.length;

                    textoUnidades.forEach((text, i) => {
                        linhaCabecalho.getCell(i + 2).value = text;
                        linhaCabecalho.getCell(i + 2).alignment = algCenter;
                    });

                    linhaCabecalho.getCell(fimColunas + 2).value = 'Total Gastos';
                    linhaCabecalho.getCell(fimColunas + 2).alignment = algCenter;
                    linhaCabecalho.getCell(fimColunas + 3).value = 'Composição';
                    linhaCabecalho.getCell(fimColunas + 3).alignment = algCenter;
                    linhaCabecalho.getCell(fimColunas + 4).value = 'Orçado';
                    linhaCabecalho.getCell(fimColunas + 4).alignment = algCenter;
                    linhaCabecalho.getCell(fimColunas + 5).value = 'Diferença';
                    linhaCabecalho.getCell(fimColunas + 5).alignment = algCenter;
                    linhaCabecalho.getCell(fimColunas + 6).value = 'Orç. Atual';
                    linhaCabecalho.getCell(fimColunas + 6).alignment = algCenter;
                    linhaCabecalho.getCell(fimColunas + 7).value = 'Variação';
                    linhaCabecalho.getCell(fimColunas + 7).alignment = algCenter;
                    linhaCabecalho.getCell(fimColunas + 8).value = 'Observação';
                    linhaCabecalho.getCell(fimColunas + 8).alignment = algCenter;

                    const dadosSortidos = dados.sort((a, b) => {
                        if (a.recurso_codigo < b.recurso_codigo) return -1;
                        if (a.recurso_codigo > b.recurso_codigo) return 1;

                        if (a.despesa_codigo < b.despesa_codigo) return -1;
                        if (a.despesa_codigo > b.despesa_codigo) return 1;

                        if (a.sub_codigo < b.sub_codigo) return -1;
                        if (a.sub_codigo > b.sub_codigo) return 1;

                        return 0;
                    });

                    let linha = 5;

                    // Mostrando dados
                    const grupoRecursos = this.funcaoService.agrupar(dadosSortidos, ['recurso_codigo', 'recurso_nome']);

                    for (const grupoRecurso of grupoRecursos) {
                        const totalizadorRecursoGeral = new Map<string, number>();
                        let orcadoTotalRecurso = 0;
                        let orcadoAtualTotalRecurso = 0;

                        grupoUnidades.forEach(unidade => {
                            const total = (grupoRecurso.registros as DAGCompleta[]).reduce((prev, cur) => {
                                if (cur.uo_codigo === unidade.grupo['uo_codigo']) {
                                    return prev + +cur.liquidado;
                                }
                                return prev;
                            }, 0);

                            const orcado = (grupoRecurso.registros as DAGCompleta[]).reduce((prev, cur) => {
                                if (cur.uo_codigo === unidade.grupo['uo_codigo']) {
                                    return prev + +cur.orcado;
                                }
                                return prev;
                            }, 0);

                            const orcadoAtual = (grupoRecurso.registros as DAGCompleta[]).reduce((prev, cur) => {
                                if (cur.uo_codigo === unidade.grupo['uo_codigo']) {
                                    return prev + +cur.orcado_atual;
                                }
                                return prev;
                            }, 0);

                            totalizadorRecursoGeral.set(unidade.grupo['uo_codigo'], total);
                            orcadoTotalRecurso = orcado;
                            orcadoAtualTotalRecurso = orcadoAtual;
                        });

                        // Printar totalizador recurso
                        const linhaValoresRecurso = ws.getRow(linha);

                        linhaValoresRecurso.getCell(1).value = `FONTE DE RECURSOS: ${grupoRecurso.grupo['recurso_codigo']} - ${grupoRecurso.grupo['recurso_nome']}`;
                        linhaValoresRecurso.getCell(1).alignment = algLeft;

                        grupoUnidades.forEach((unidade, i) => {
                            linhaValoresRecurso.getCell(i + 2).value = totalizadorRecursoGeral.get(unidade.grupo['uo_codigo']);
                            linhaValoresRecurso.getCell(i + 2).alignment = algRight;
                        });

                        let totalGeralRecurso = 0;

                        totalizadorRecursoGeral.forEach(v => {
                            totalGeralRecurso += v;
                        });

                        linhaValoresRecurso.getCell(fimColunas + 2).value = +totalGeralRecurso.toFixed(2);
                        linhaValoresRecurso.getCell(fimColunas + 2).alignment = algRight;
                        linhaValoresRecurso.getCell(fimColunas + 3).value = '100,00%';
                        linhaValoresRecurso.getCell(fimColunas + 3).alignment = algRight;
                        linhaValoresRecurso.getCell(fimColunas + 4).value = +orcadoTotalRecurso.toFixed(2);
                        linhaValoresRecurso.getCell(fimColunas + 4).alignment = algRight;
                        linhaValoresRecurso.getCell(fimColunas + 5).value = +(orcadoTotalRecurso - totalGeralRecurso).toFixed(2);
                        linhaValoresRecurso.getCell(fimColunas + 5).alignment = algRight;
                        linhaValoresRecurso.getCell(fimColunas + 6).value = +orcadoAtualTotalRecurso.toFixed(2);
                        linhaValoresRecurso.getCell(fimColunas + 6).alignment = algRight;
                        linhaValoresRecurso.getCell(fimColunas + 7).value = !+orcadoAtualTotalRecurso ? '0,00%' : `${+((totalGeralRecurso / +orcadoAtualTotalRecurso) * 100).toFixed(2)}%`;
                        linhaValoresRecurso.getCell(fimColunas + 7).alignment = algRight;

                        if (formato === 'pdf') {
                            linhasPdf.push(this.montarLinhaPdf(
                                `FONTE DE RECURSOS: ${grupoRecurso.grupo['recurso_codigo']} - ${grupoRecurso.grupo['recurso_nome']}`,
                                grupoUnidades.map(unidade => totalizadorRecursoGeral.get(unidade.grupo['uo_codigo'])),
                                totalGeralRecurso, '100,00%', orcadoTotalRecurso, orcadoTotalRecurso - totalGeralRecurso, orcadoAtualTotalRecurso,
                                !+orcadoAtualTotalRecurso ? '0,00%' : `${+((totalGeralRecurso / +orcadoAtualTotalRecurso) * 100).toFixed(2)}%`,
                                true, fontSizePdf
                            ));
                        }

                        linha++;

                        const grupoDespPais = this.funcaoService.agrupar(grupoRecurso.registros, ['despesa_pai_codigo', 'despesa_pai_nome']);

                        for (const grupoDespPai of grupoDespPais) {
                            const totalizadorDespPaiGeral = new Map<string, number>();
                            let orcadoTotalPai = 0;
                            let orcadoAtualTotalPai = 0;

                            grupoUnidades.forEach(unidade => {
                                const total = (grupoDespPai.registros as DAGCompleta[]).reduce((prev, cur) => {
                                    if (cur.uo_codigo === unidade.grupo['uo_codigo']) {
                                        return prev + +cur.liquidado;
                                    }
                                    return prev;
                                }, 0);

                                const orcado = (grupoDespPai.registros as DAGCompleta[]).reduce((prev, cur) => {
                                    if (cur.uo_codigo === unidade.grupo['uo_codigo']) {
                                        return prev + +cur.orcado;
                                    }
                                    return prev;
                                }, 0);

                                const orcadoAtual = (grupoDespPai.registros as DAGCompleta[]).reduce((prev, cur) => {
                                    if (cur.uo_codigo === unidade.grupo['uo_codigo']) {
                                        return prev + +cur.orcado_atual;
                                    }
                                    return prev;
                                }, 0);

                                totalizadorDespPaiGeral.set(unidade.grupo['uo_codigo'], total);
                                orcadoTotalPai = orcado;
                                orcadoAtualTotalPai = orcadoAtual;
                            });

                            // Printar totalizador da despesa pai
                            const linhaValoresDespesaPai = ws.getRow(linha);
                            linhaValoresDespesaPai.getCell(1).value = `  ${grupoDespPai.grupo['despesa_pai_codigo']} ${grupoDespPai.grupo['despesa_pai_nome']}`;
                            linhaValoresDespesaPai.getCell(1).alignment = algLeft;

                            grupoUnidades.forEach((unidade, i) => {
                                linhaValoresDespesaPai.getCell(i + 2).value = totalizadorDespPaiGeral.get(unidade.grupo['uo_codigo']);
                                linhaValoresDespesaPai.getCell(i + 2).alignment = algRight;
                            });

                            let totalGeralPai = 0;

                            totalizadorDespPaiGeral.forEach(v => {
                                totalGeralPai += v;
                            });

                            linhaValoresDespesaPai.getCell(fimColunas + 2).value = +totalGeralPai.toFixed(2);
                            linhaValoresDespesaPai.getCell(fimColunas + 2).alignment = algRight;
                            linhaValoresDespesaPai.getCell(fimColunas + 3).value = '100,00%';
                            linhaValoresDespesaPai.getCell(fimColunas + 3).alignment = algRight;
                            linhaValoresDespesaPai.getCell(fimColunas + 4).value = +orcadoTotalPai.toFixed(2);
                            linhaValoresDespesaPai.getCell(fimColunas + 4).alignment = algRight;
                            linhaValoresDespesaPai.getCell(fimColunas + 5).value = +(orcadoTotalPai - totalGeralPai).toFixed(2);
                            linhaValoresDespesaPai.getCell(fimColunas + 5).alignment = algRight;
                            linhaValoresDespesaPai.getCell(fimColunas + 6).value = +orcadoAtualTotalPai.toFixed(2);
                            linhaValoresDespesaPai.getCell(fimColunas + 6).alignment = algRight;
                            linhaValoresDespesaPai.getCell(fimColunas + 7).value = !+orcadoAtualTotalPai ? '0,00%' : `${+((totalGeralPai / +orcadoAtualTotalPai) * 100).toFixed(2)}%`;
                            linhaValoresDespesaPai.getCell(fimColunas + 7).alignment = algRight;

                            if (formato === 'pdf') {
                                linhasPdf.push(this.montarLinhaPdf(
                                    `  ${grupoDespPai.grupo['despesa_pai_codigo']} ${grupoDespPai.grupo['despesa_pai_nome']}`,
                                    grupoUnidades.map(unidade => totalizadorDespPaiGeral.get(unidade.grupo['uo_codigo'])),
                                    totalGeralPai, '100,00%', orcadoTotalPai, orcadoTotalPai - totalGeralPai, orcadoAtualTotalPai,
                                    !+orcadoAtualTotalPai ? '0,00%' : `${+((totalGeralPai / +orcadoAtualTotalPai) * 100).toFixed(2)}%`,
                                    true, fontSizePdf
                                ));
                            }

                            linha++;

                            // Agrupar por despesa
                            const grupoDespesaFilhas = this.funcaoService.agrupar(grupoDespPai.registros, ['despesa_codigo', 'despesa_nome']);

                            for (const grupoDespesaFilha of grupoDespesaFilhas) {
                                // Printar despesas filhas
                                const linhaValoresDespesaFilha = ws.getRow(linha);
                                linhaValoresDespesaFilha.getCell(1).value = `    ${grupoDespesaFilha.grupo['despesa_codigo']} ${grupoDespesaFilha.grupo['despesa_nome']}`;
                                linhaValoresDespesaFilha.getCell(1).alignment = algLeft;

                                let totalGeral = 0;

                                grupoUnidades.forEach((unidade, i) => {
                                    const despesas = (grupoDespesaFilha.registros as DAGCompleta[]).filter(d => d.uo_codigo == unidade.grupo['uo_codigo']);
                                    let liquidado = 0;
                                    for (const despesa of despesas) {
                                        liquidado += +despesa.liquidado;
                                    }
                                    linhaValoresDespesaFilha.getCell(i + 2).value = +liquidado;
                                    linhaValoresDespesaFilha.getCell(i + 2).alignment = algRight;

                                    totalGeral += +liquidado;
                                });

                                const orcado = +(grupoDespesaFilha.registros as DAGCompleta[])[0].orcado;
                                const orcado_atual = +(grupoDespesaFilha.registros as DAGCompleta[])[0].orcado_atual;


                                linhaValoresDespesaFilha.getCell(fimColunas + 2).value = +totalGeral.toFixed(2);
                                linhaValoresDespesaFilha.getCell(fimColunas + 2).alignment = algRight;
                                linhaValoresDespesaFilha.getCell(fimColunas + 3).value = !totalGeralPai ? '0,00%' : `${+((totalGeral / totalGeralPai) * 100).toFixed(2)}%`;
                                linhaValoresDespesaFilha.getCell(fimColunas + 3).alignment = algRight;
                                linhaValoresDespesaFilha.getCell(fimColunas + 4).value = +orcado.toFixed(2);
                                linhaValoresDespesaFilha.getCell(fimColunas + 4).alignment = algRight;
                                linhaValoresDespesaFilha.getCell(fimColunas + 5).value = +(orcado - totalGeral).toFixed(2);
                                linhaValoresDespesaFilha.getCell(fimColunas + 5).alignment = algRight;
                                linhaValoresDespesaFilha.getCell(fimColunas + 6).value = +orcado_atual.toFixed(2);
                                linhaValoresDespesaFilha.getCell(fimColunas + 6).alignment = algRight;
                                linhaValoresDespesaFilha.getCell(fimColunas + 7).value = !orcado_atual ? '0,00%' : `${+((totalGeral / orcado_atual) * 100).toFixed(2)}%`;
                                linhaValoresDespesaFilha.getCell(fimColunas + 7).alignment = algRight;

                                if (formato === 'pdf') {
                                    linhasPdf.push(this.montarLinhaPdf(
                                        `    ${grupoDespesaFilha.grupo['despesa_codigo']} ${grupoDespesaFilha.grupo['despesa_nome']}`,
                                        grupoUnidades.map((unidade) => (grupoDespesaFilha.registros as DAGCompleta[]).filter(d => d.uo_codigo == unidade.grupo['uo_codigo']).reduce((prev, cur) => prev + +cur.liquidado, 0)),
                                        totalGeral, !totalGeralPai ? '0,00%' : `${+((totalGeral / totalGeralPai) * 100).toFixed(2)}%`, orcado, orcado - totalGeral, orcado_atual,
                                        !orcado_atual ? '0,00%' : `${+((totalGeral / orcado_atual) * 100).toFixed(2)}%`,
                                        false, fontSizePdf
                                    ));
                                }

                                linha++;

                                const grupoSubelementos = this.funcaoService.agrupar(grupoDespesaFilha.registros, ['despesa_codigo', 'despesa_nome', 'sub_codigo', 'sub_nome']);

                                for (const grupoSubelemento of grupoSubelementos) {
                                    // Printar despesas filhas
                                    try {
                                        if (grupoSubelemento.grupo['sub_codigo'] !== '0') {
                                            const linhaValoresSubelemento = ws.getRow(linha);
                                            linhaValoresSubelemento.getCell(1).value = `        ${grupoSubelemento.grupo['sub_codigo']} ${grupoSubelemento.grupo['sub_nome']}`;
                                            linhaValoresSubelemento.getCell(1).alignment = algLeft;

                                            let totalSub = 0;

                                            grupoUnidades.forEach((unidade, i) => {
                                                const despesa = (grupoSubelemento.registros as DAGCompleta[]).find(d => d.uo_codigo == unidade.grupo['uo_codigo']);
                                                if (despesa) {
                                                    linhaValoresSubelemento.getCell(i + 2).value = +despesa.liquidado;
                                                    linhaValoresSubelemento.getCell(i + 2).alignment = algRight;
                                                    totalSub += +despesa.liquidado;
                                                } else {
                                                    linhaValoresSubelemento.getCell(i + 2).value = +0;
                                                    linhaValoresSubelemento.getCell(i + 2).alignment = algRight;
                                                }
                                            });

                                            linhaValoresSubelemento.getCell(fimColunas + 2).value = +totalSub.toFixed(2);
                                            linhaValoresSubelemento.getCell(fimColunas + 2).alignment = algRight;
                                            linhaValoresSubelemento.getCell(fimColunas + 3).value = !totalGeralPai ? '0,00%' : `${+((totalSub / totalGeralPai) * 100).toFixed(2)}%`;
                                            linhaValoresSubelemento.getCell(fimColunas + 3).alignment = algRight;
                                            linhaValoresSubelemento.getCell(fimColunas + 4).value = '-';
                                            linhaValoresSubelemento.getCell(fimColunas + 4).alignment = algRight;
                                            linhaValoresSubelemento.getCell(fimColunas + 5).value = '-';
                                            linhaValoresSubelemento.getCell(fimColunas + 5).alignment = algRight;
                                            linhaValoresSubelemento.getCell(fimColunas + 6).value = '-';
                                            linhaValoresSubelemento.getCell(fimColunas + 6).alignment = algRight;
                                            linhaValoresSubelemento.getCell(fimColunas + 7).value = !orcado_atual ? '0,00%' : `${+((totalSub / orcado_atual) * 100).toFixed(2)}%`;;
                                            linhaValoresSubelemento.getCell(fimColunas + 7).alignment = algRight;

                                            if (formato === 'pdf') {
                                                linhasPdf.push(this.montarLinhaPdf(
                                                    `        ${grupoSubelemento.grupo['sub_codigo']} ${grupoSubelemento.grupo['sub_nome']}`,
                                                    grupoUnidades.map(unidade => {
                                                        const despesa = (grupoSubelemento.registros as DAGCompleta[]).find(d => d.uo_codigo == unidade.grupo['uo_codigo']);
                                                        return despesa ? +despesa.liquidado : 0;
                                                    }),
                                                    totalSub, !totalGeralPai ? '0,00%' : `${+((totalSub / totalGeralPai) * 100).toFixed(2)}%`, '-', '-', '-',
                                                    !orcado_atual ? '0,00%' : `${+((totalSub / orcado_atual) * 100).toFixed(2)}%`,
                                                    false, fontSizePdf
                                                ));
                                            }

                                            linha++;
                                        }
                                    } catch (err) { console.error(err); }
                                }
                            }
                        }
                    }

                    // Quadro consolidado
                    const dadosConsolidadosSortidos = dados.sort((a, b) => {
                        if (a.despesa_codigo < b.despesa_codigo) return -1;
                        if (a.despesa_codigo > b.despesa_codigo) return 1;

                        return 0;
                    });

                    const grupoConsolidadoDespPais = this.funcaoService.agrupar(dadosConsolidadosSortidos, ['despesa_pai_codigo', 'despesa_pai_nome']);

                    ws.getCell(linha, 1).value = 'FONTE DE RECURSOS: CONSOLIDADO';
                    ws.getCell(linha, 1).alignment = algLeft;

                    if (formato === 'pdf') {
                        linhasPdf.push([
                            { text: 'FONTE DE RECURSOS: CONSOLIDADO', bold: true, fontSize: 8, alignment: 'center', colSpan: totalColunasPdf },
                            ...Array(totalColunasPdf - 1).fill('')
                        ]);
                    }

                    linha++;

                    const totalizadorLinhaGeral = new Map<string, number>();

                    // Inicia o totalizador com 0 para ser usado como somatório lá embaixo.
                    grupoUnidades.forEach(unidade => {
                        totalizadorLinhaGeral.set(unidade.grupo['uo_codigo'], 0);
                    });

                    let totalizadorGeral = 0;
                    let totalizadorOrcado = 0;
                    let totalizadorOrcadoAtual = 0;

                    for (const grupoConsolidadoDespPai of grupoConsolidadoDespPais) {
                        const totalizadorConsoDespPaiGeral = new Map<string, number>();
                        let orcadoTotalConsoPai = 0;
                        let orcadoAtualTotalConsoPai = 0;

                        grupoUnidades.forEach(unidade => {
                            const total = (grupoConsolidadoDespPai.registros as DAGCompleta[]).reduce((prev, cur) => {
                                if (cur.uo_codigo === unidade.grupo['uo_codigo']) {
                                    return prev + +cur.liquidado;
                                }
                                return prev;
                            }, 0);

                            const orcado = (grupoConsolidadoDespPai.registros as DAGCompleta[]).reduce((prev, cur) => {
                                if (cur.uo_codigo === unidade.grupo['uo_codigo']) {
                                    return prev + +cur.orcado;
                                }
                                return prev;
                            }, 0);

                            const orcadoAtual = (grupoConsolidadoDespPai.registros as DAGCompleta[]).reduce((prev, cur) => {
                                if (cur.uo_codigo === unidade.grupo['uo_codigo']) {
                                    return prev + +cur.orcado_atual;
                                }
                                return prev;
                            }, 0);

                            totalizadorConsoDespPaiGeral.set(unidade.grupo['uo_codigo'], total);
                            orcadoTotalConsoPai = orcado;
                            orcadoAtualTotalConsoPai = orcadoAtual;
                        });

                        // Printar totalizador da despesa pai
                        const linhaValoresDespesaConsoPai = ws.getRow(linha);
                        linhaValoresDespesaConsoPai.getCell(1).value = `  ${grupoConsolidadoDespPai.grupo['despesa_pai_codigo']} ${grupoConsolidadoDespPai.grupo['despesa_pai_nome']}`;
                        linhaValoresDespesaConsoPai.getCell(1).alignment = algLeft;

                        grupoUnidades.forEach((unidade, i) => {
                            const valorLinha = totalizadorConsoDespPaiGeral.get(unidade.grupo['uo_codigo']);
                            linhaValoresDespesaConsoPai.getCell(i + 2).value = valorLinha
                            linhaValoresDespesaConsoPai.getCell(i + 2).alignment = algRight;
                            totalizadorLinhaGeral.set(unidade.grupo['uo_codigo'], totalizadorLinhaGeral.get(unidade.grupo['uo_codigo']) + valorLinha);
                        });

                        let totalGeralConsoPai = 0;

                        totalizadorConsoDespPaiGeral.forEach(v => {
                            totalGeralConsoPai += v;
                        });
                        totalizadorGeral += totalGeralConsoPai;

                        totalizadorOrcado += orcadoTotalConsoPai;
                        totalizadorOrcadoAtual += orcadoAtualTotalConsoPai;

                        linhaValoresDespesaConsoPai.getCell(fimColunas + 2).value = +totalGeralConsoPai.toFixed(2);
                        linhaValoresDespesaConsoPai.getCell(fimColunas + 2).alignment = algRight;
                        linhaValoresDespesaConsoPai.getCell(fimColunas + 3).value = '100,00%';
                        linhaValoresDespesaConsoPai.getCell(fimColunas + 3).alignment = algRight;
                        linhaValoresDespesaConsoPai.getCell(fimColunas + 4).value = +orcadoTotalConsoPai.toFixed(2);
                        linhaValoresDespesaConsoPai.getCell(fimColunas + 4).alignment = algRight;
                        linhaValoresDespesaConsoPai.getCell(fimColunas + 5).value = +(orcadoTotalConsoPai - totalGeralConsoPai).toFixed(2);
                        linhaValoresDespesaConsoPai.getCell(fimColunas + 5).alignment = algRight;
                        linhaValoresDespesaConsoPai.getCell(fimColunas + 6).value = +orcadoAtualTotalConsoPai.toFixed(2);
                        linhaValoresDespesaConsoPai.getCell(fimColunas + 6).alignment = algRight;
                        linhaValoresDespesaConsoPai.getCell(fimColunas + 7).value = !+orcadoAtualTotalConsoPai ? '0,00%' : `${+((totalGeralConsoPai / +orcadoAtualTotalConsoPai) * 100).toFixed(2)}%`;
                        linhaValoresDespesaConsoPai.getCell(fimColunas + 7).alignment = algRight;

                        if (formato === 'pdf') {
                            linhasPdf.push(this.montarLinhaPdf(
                                `  ${grupoConsolidadoDespPai.grupo['despesa_pai_codigo']} ${grupoConsolidadoDespPai.grupo['despesa_pai_nome']}`,
                                grupoUnidades.map(unidade => totalizadorConsoDespPaiGeral.get(unidade.grupo['uo_codigo'])),
                                totalGeralConsoPai, '100,00%', orcadoTotalConsoPai, orcadoTotalConsoPai - totalGeralConsoPai, orcadoAtualTotalConsoPai,
                                !+orcadoAtualTotalConsoPai ? '0,00%' : `${+((totalGeralConsoPai / +orcadoAtualTotalConsoPai) * 100).toFixed(2)}%`,
                                true, fontSizePdf
                            ));
                        }

                        linha++;

                        // Agrupar por despesa
                        const grupoDespesaConsoFilhas = this.funcaoService.agrupar(grupoConsolidadoDespPai.registros, ['despesa_codigo', 'despesa_nome']);

                        for (const grupoDespesaConsoFilha of grupoDespesaConsoFilhas) {
                            // Printar despesas filhas
                            const linhaValoresDespesaFilha = ws.getRow(linha);
                            linhaValoresDespesaFilha.getCell(1).value = `    ${grupoDespesaConsoFilha.grupo['despesa_codigo']} ${grupoDespesaConsoFilha.grupo['despesa_nome']}`;
                            linhaValoresDespesaFilha.getCell(1).alignment = algLeft;

                            let totalGeral = 0;

                            grupoUnidades.forEach((unidade, i) => {
                                const despesas = (grupoDespesaConsoFilha.registros as DAGCompleta[]).filter(d => d.uo_codigo == unidade.grupo['uo_codigo']);
                                let liquidado = 0;
                                for (const despesa of despesas) {
                                    liquidado += +despesa.liquidado;
                                }
                                linhaValoresDespesaFilha.getCell(i + 2).value = +liquidado;
                                linhaValoresDespesaFilha.getCell(i + 2).alignment = algRight;

                                totalGeral += +liquidado;
                            });

                            const orcado = +(grupoDespesaConsoFilha.registros as DAGCompleta[])[0].orcado;
                            const orcado_atual = +(grupoDespesaConsoFilha.registros as DAGCompleta[])[0].orcado_atual;


                            linhaValoresDespesaFilha.getCell(fimColunas + 2).value = +totalGeral.toFixed(2);
                            linhaValoresDespesaFilha.getCell(fimColunas + 2).alignment = algRight;
                            linhaValoresDespesaFilha.getCell(fimColunas + 3).value = !totalGeralConsoPai ? '0,00%' : `${+((totalGeral / totalGeralConsoPai) * 100).toFixed(2)}%`;
                            linhaValoresDespesaFilha.getCell(fimColunas + 3).alignment = algRight;
                            linhaValoresDespesaFilha.getCell(fimColunas + 4).value = +orcado.toFixed(2);
                            linhaValoresDespesaFilha.getCell(fimColunas + 4).alignment = algRight;
                            linhaValoresDespesaFilha.getCell(fimColunas + 5).value = +(orcado - totalGeral).toFixed(2);
                            linhaValoresDespesaFilha.getCell(fimColunas + 5).alignment = algRight;
                            linhaValoresDespesaFilha.getCell(fimColunas + 6).value = +orcado_atual.toFixed(2);
                            linhaValoresDespesaFilha.getCell(fimColunas + 6).alignment = algRight;
                            linhaValoresDespesaFilha.getCell(fimColunas + 7).value = !orcadoAtualTotalConsoPai ? '0,00%' : `${+((totalGeral / orcadoAtualTotalConsoPai) * 100).toFixed(2)}%`;
                            linhaValoresDespesaFilha.getCell(fimColunas + 7).alignment = algRight;

                            if (formato === 'pdf') {
                                linhasPdf.push(this.montarLinhaPdf(
                                    `    ${grupoDespesaConsoFilha.grupo['despesa_codigo']} ${grupoDespesaConsoFilha.grupo['despesa_nome']}`,
                                    grupoUnidades.map((unidade) => (grupoDespesaConsoFilha.registros as DAGCompleta[]).filter(d => d.uo_codigo == unidade.grupo['uo_codigo']).reduce((prev, cur) => prev + +cur.liquidado, 0)),
                                    totalGeral, !totalGeralConsoPai ? '0,00%' : `${+((totalGeral / totalGeralConsoPai) * 100).toFixed(2)}%`, orcado, orcado - totalGeral, orcado_atual,
                                    !orcadoAtualTotalConsoPai ? '0,00%' : `${+((totalGeral / orcadoAtualTotalConsoPai) * 100).toFixed(2)}%`,
                                    false, fontSizePdf
                                ));
                            }

                            linha++;

                            const grupoConsoSubelementos = this.funcaoService.agrupar(grupoDespesaConsoFilha.registros, ['despesa_codigo', 'despesa_nome', 'sub_codigo', 'sub_nome']);

                            for (const grupoConsoSubelemento of grupoConsoSubelementos) {
                                try {
                                    if (grupoConsoSubelemento.grupo['sub_codigo'] !== '0') {
                                        const linhaValoresSubelemento = ws.getRow(linha);
                                        linhaValoresSubelemento.getCell(1).value = `        ${grupoConsoSubelemento.grupo['sub_codigo']} ${grupoConsoSubelemento.grupo['sub_nome']}`;
                                        linhaValoresSubelemento.getCell(1).alignment = algLeft;

                                        let totalSub = 0;

                                        grupoUnidades.forEach((unidade, i) => {
                                            const despesa = (grupoConsoSubelemento.registros as DAGCompleta[]).find(d => d.uo_codigo == unidade.grupo['uo_codigo']);
                                            if (despesa) {
                                                linhaValoresSubelemento.getCell(i + 2).value = +despesa.liquidado;
                                                linhaValoresSubelemento.getCell(i + 2).alignment = algRight;
                                                totalSub += +despesa.liquidado;
                                            } else {
                                                linhaValoresSubelemento.getCell(i + 2).value = +0;
                                                linhaValoresSubelemento.getCell(i + 2).alignment = algRight;
                                            }
                                        });

                                        linhaValoresSubelemento.getCell(fimColunas + 2).value = +totalSub.toFixed(2);
                                        linhaValoresSubelemento.getCell(fimColunas + 2).alignment = algRight;
                                        linhaValoresSubelemento.getCell(fimColunas + 3).value = !totalGeralConsoPai ? '0,00%' : `${+((totalSub / totalGeralConsoPai) * 100).toFixed(2)}%`;
                                        linhaValoresSubelemento.getCell(fimColunas + 3).alignment = algRight;
                                        linhaValoresSubelemento.getCell(fimColunas + 4).value = '-';
                                        linhaValoresSubelemento.getCell(fimColunas + 4).alignment = algRight;
                                        linhaValoresSubelemento.getCell(fimColunas + 5).value = '-';
                                        linhaValoresSubelemento.getCell(fimColunas + 5).alignment = algRight;
                                        linhaValoresSubelemento.getCell(fimColunas + 6).value = '-';
                                        linhaValoresSubelemento.getCell(fimColunas + 6).alignment = algRight;
                                        linhaValoresSubelemento.getCell(fimColunas + 7).value = !orcado_atual ? '0,00%' : `${+((totalSub / orcado_atual) * 100).toFixed(2)}%`;;
                                        linhaValoresSubelemento.getCell(fimColunas + 7).alignment = algRight;

                                        if (formato === 'pdf') {
                                            linhasPdf.push(this.montarLinhaPdf(
                                                `        ${grupoConsoSubelemento.grupo['sub_codigo']} ${grupoConsoSubelemento.grupo['sub_nome']}`,
                                                grupoUnidades.map(unidade => {
                                                    const despesa = (grupoConsoSubelemento.registros as DAGCompleta[]).find(d => d.uo_codigo == unidade.grupo['uo_codigo']);
                                                    return despesa ? +despesa.liquidado : 0;
                                                }),
                                                totalSub, !totalGeralConsoPai ? '0,00%' : `${+((totalSub / totalGeralConsoPai) * 100).toFixed(2)}%`, '-', '-', '-',
                                                !orcado_atual ? '0,00%' : `${+((totalSub / orcado_atual) * 100).toFixed(2)}%`,
                                                false, fontSizePdf
                                            ));
                                        }

                                        linha++;
                                    }
                                } catch (err) { console.error(err); }
                            }
                        }
                    }

                    const linhaTotalGeral = ws.getRow(linha);

                    linhaTotalGeral.getCell(1).value = 'Total Geral';
                    linhaTotalGeral.getCell(1).alignment = algLeft;

                    grupoUnidades.forEach((unidade, i) => {
                        linhaTotalGeral.getCell(i + 2).value = totalizadorLinhaGeral.get(unidade.grupo['uo_codigo']);
                        linhaTotalGeral.getCell(i + 2).alignment = algRight;
                    });

                    linhaTotalGeral.getCell(fimColunas + 2).value = +totalizadorGeral.toFixed(2);
                    linhaTotalGeral.getCell(fimColunas + 2).alignment = algRight;
                    linhaTotalGeral.getCell(fimColunas + 3).value = '100,00%'
                    linhaTotalGeral.getCell(fimColunas + 3).alignment = algRight;
                    linhaTotalGeral.getCell(fimColunas + 4).value = +totalizadorOrcado.toFixed(2);
                    linhaTotalGeral.getCell(fimColunas + 4).alignment = algRight;
                    linhaTotalGeral.getCell(fimColunas + 5).value = +(totalizadorOrcado - totalizadorGeral).toFixed(2);
                    linhaTotalGeral.getCell(fimColunas + 5).alignment = algRight;
                    linhaTotalGeral.getCell(fimColunas + 6).value = +totalizadorOrcadoAtual.toFixed(2);
                    linhaTotalGeral.getCell(fimColunas + 6).alignment = algRight;
                    linhaTotalGeral.getCell(fimColunas + 7).value = !totalizadorOrcadoAtual ? '0,00%' : `${+((totalizadorGeral / totalizadorOrcadoAtual) * 100).toFixed(2)}%`;
                    linhaTotalGeral.getCell(fimColunas + 7).alignment = algRight;

                    // Criando Arquivo
                    if (formato === 'pdf') {
                        linhasPdf.push(this.montarLinhaPdf(
                            'Total Geral',
                            grupoUnidades.map(unidade => totalizadorLinhaGeral.get(unidade.grupo['uo_codigo'])),
                            totalizadorGeral, '100,00%', totalizadorOrcado, totalizadorOrcado - totalizadorGeral, totalizadorOrcadoAtual,
                            !totalizadorOrcadoAtual ? '0,00%' : `${+((totalizadorGeral / totalizadorOrcadoAtual) * 100).toFixed(2)}%`,
                            true, fontSizeTotalPdf, true
                        ));

                        const cabecalhoPdf: any[] = [
                            { text: 'Classificação', bold: true, fontSize: fontSizePdf },
                            ...textoUnidades.map(texto => ({ text: texto, bold: true, fontSize: fontSizePdf, alignment: 'center' })),
                            { text: 'Total Gastos', bold: true, fontSize: fontSizePdf, alignment: 'center' },
                            { text: 'Composição', bold: true, fontSize: fontSizePdf, alignment: 'center' },
                            { text: 'Orçado', bold: true, fontSize: fontSizePdf, alignment: 'center' },
                            { text: 'Diferença', bold: true, fontSize: fontSizePdf, alignment: 'center' },
                            { text: 'Orç. Atual', bold: true, fontSize: fontSizePdf, alignment: 'center' },
                            { text: 'Variação', bold: true, fontSize: fontSizePdf, alignment: 'center' }
                        ];

                        const conteudoPdf: {}[] = [
                            {
                                layout: 'linhasDag',
                                table: {
                                    dontBreakRows: true,
                                    headerRows: 1,
                                    widths: [
                                        larguraClassificacaoPdf,
                                        ...grupoUnidades.map(() => larguraColunaUnidadePdf),
                                        larguraColunaTotalPdf, larguraColunaPercentualPdf, larguraColunaTotalPdf,
                                        larguraColunaTotalPdf, larguraColunaTotalPdf, larguraColunaPercentualPdf
                                    ],
                                    body: [cabecalhoPdf, ...linhasPdf]
                                }
                            }
                        ];

                        Relatorio.imprimirPersonalizado(
                            'Demonstrações Auxiliares de Gastos - DAGs', this.login.usuario.nome, this.login.usuario.sobrenome,
                            this.login.orgao.nome, this.login.brasao, conteudoPdf, 'landscape', 'demonstrativo-auxiliar-de-gastos',
                            {
                                linhasDag: {
                                    hLineWidth() { return 1; },
                                    vLineWidth() { return 1; },
                                    hLineColor() { return 'black'; },
                                    paddingLeft() { return 1; },
                                    paddingRight() { return 1; }
                                }
                            }, false, false, null, subtituloTexto,
                            [10, this.login.brasao ? 140 : 80, 10, 40]
                        );
                    } else if (formato === 'csv') {
                        const csv = this.montarCsv(ws, linha, fimColunas + 8);

                        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });

                        const downloadURL = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = downloadURL;
                        link.download = `demonstrativo-auxiliar-de-gastos.csv`;
                        link.target = '_blank';
                        link.click();
                        window.URL.revokeObjectURL(downloadURL);
                    } else {
                        wb.xlsx.writeBuffer().then((data) => {
                            let blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

                            const downloadURL = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = downloadURL;
                            link.download = `demonstrativo-auxiliar-de-gastos.xlsx`;
                            link.target = '_blank';
                            link.click();
                            window.URL.revokeObjectURL(downloadURL);
                        })
                    }
                })
            });
    };

    private montarCsv(ws: any, totalLinhas: number, totalColunas: number): string {
        const linhas: string[] = [];

        for (let l = 1; l <= totalLinhas; l++) {
            const campos: string[] = [];
            for (let c = 1; c <= totalColunas; c++) {
                campos.push(this.formatarCampoCsv(ws.getCell(l, c).value));
            }
            linhas.push(campos.join(';'));
        }

        return linhas.join('\r\n');
    }

    private formatarCampoCsv(valor: any): string {
        if (valor === null || valor === undefined) return '';

        // células montadas pelo exceljs podem guardar objetos (fórmula, hyperlink, rich text)
        if (typeof valor === 'object') {
            valor = valor.result ?? valor.text ?? valor.hyperlink ?? '';
        }

        const texto = typeof valor === 'number'
            ? this.funcaoService.convertToBrNumber(valor)
            : `${valor}`;

        return /[";\r\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
    }

    private montarLinhaPdf(
        label: string,
        valores: number[],
        totalGeral: number,
        composicao: string,
        orcado: number | string,
        diferenca: number | string,
        orcadoAtual: number | string,
        variacao: string,
        negrito: boolean,
        fontSize: number,
        quebrarLinha: boolean = false
    ): any[] {
        const fmt = (v: number | string) => typeof v === 'number' ? this.funcaoService.convertToBrNumber(v) : v;
        const noWrap = !quebrarLinha;
        return [
            { text: label, bold: negrito, fontSize },
            ...valores.map(v => ({ text: this.funcaoService.convertToBrNumber(v), alignment: 'right', bold: negrito, fontSize, noWrap })),
            { text: fmt(totalGeral), alignment: 'right', bold: negrito, fontSize, noWrap },
            { text: composicao, alignment: 'right', bold: negrito, fontSize, noWrap },
            { text: fmt(orcado), alignment: 'right', bold: negrito, fontSize, noWrap },
            { text: fmt(diferenca), alignment: 'right', bold: negrito, fontSize, noWrap },
            { text: fmt(orcadoAtual), alignment: 'right', bold: negrito, fontSize, noWrap },
            { text: variacao, alignment: 'right', bold: negrito, fontSize, noWrap }
        ];
    }

};
